import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { StorageService } from './storage-service';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from './notification.service';
import { AuthTokenModel } from '../common/interfaces/global-interfaces';
import { environment } from '../../environments/environment';

import { AppStoreService } from '../store/ngrx-signal.store';
import { SET_NEW_PASSWORD, FORGOT_PASSWORD, FORGOT_CHANGE_PASSWORD } from '../modules/user-profile/user-profile.models';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly storage = inject(StorageService);
  private readonly notify = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly http = inject(HttpClient);
  private readonly appStore = inject(AppStoreService);
  private readonly apollo = inject(Apollo);

  constructor() {
    this.startRefreshMonitor();
  }

  /** Check if user is logged in */
  alreadyLoggedIn(): boolean {
    if (environment?.BYPASS_LOGIN) return true;

    const token = this.storage.get<string>('access_token');
    const expiry = this.storage.get<number>('token_expires_at');

    if (!token || token === 'undefined' || token === 'null') return false;

    // If we have an expiry time, strictly enforce it
    if (expiry && Date.now() > expiry) {
      this.clearStore(); // Clean up expired session data
      return false;
    }

    return true;
  }

  /** Refresh token logic */
  async refreshToken(): Promise<AuthTokenModel | null> {
    const refresh = this.storage.get('refresh_token') as string | null;
    if (!refresh) return null;

    const body = new URLSearchParams();
    body.set('refresh_token', refresh);
    body.set('grant_type', 'refresh_token');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    });

    try {
      const token = await firstValueFrom(
        this.http.post<AuthTokenModel>(`${environment.AUTH_SERVER_URL}/o/token/`, body.toString(), {
          headers,
        }),
      );

      if (token.access_token) this.persistTokens(token);

      return token;
    } catch (err: any) {
      console.error('Failed to refresh token', err);
      // If refresh fails (especially with 400/401), we must log the user out
      if (err.status === 400 || err.status === 401) {
        this.performLocalLogout();
      }
      return null;
    }
  }

  /** Login logic */
  async login(credentials: { username: string; password: string }): Promise<AuthTokenModel | null> {
    const params = new URLSearchParams();
    params.set('username', credentials.username);
    params.set('password', credentials.password);
    params.set('grant_type', environment.GRANT_TYPE ?? 'password');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    });

    try {
      const tokenData = await firstValueFrom(
        this.http.post<AuthTokenModel>(
          `${environment.AUTH_SERVER_URL}/o/token/`,
          params.toString(),
          { headers },
        ),
      );

      if (tokenData.access_token) {
        this.persistTokens(tokenData);
        this.notify.success('Logged in successfully!');
        setTimeout(() => this.router.navigate(['module-selection'], { replaceUrl: true }), 0);
      }

      return tokenData;
    } catch (error: any) {
      this.handleLoginError(error);
      return null;
    }
  }

  /** Store access & refresh tokens safely */
  private persistTokens(token: AuthTokenModel): void {
    const expiry = Date.now() + token.expires_in * 1000;

    this.storage.set('access_token', token.access_token);
    this.storage.set('refresh_token', token.refresh_token);
    this.storage.set('token_type', token.token_type);
    this.storage.set('token_expires_at', expiry);

    this.startRefreshMonitor();
  }

  private refreshTimer?: any;

  /** Automatically refresh token before expiry */
  startRefreshMonitor(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const expiry = this.storage.get('token_expires_at') as number;
    if (!expiry) return;

    const now = Date.now();
    const refreshBeforeMs = 10_000; // 10 seconds

    const timeout = expiry - now - refreshBeforeMs;

    if (timeout <= 0) {
      // Token already expired → refresh immediately
      this.refreshToken();
      return;
    }

    this.refreshTimer = setTimeout(() => {
      if (this.alreadyLoggedIn()) {
        this.refreshToken();
      }
    }, timeout);
  }

  /** Handle common login errors */
  private handleLoginError(error: any): void {
    if (error.status === 0) {
      this.notify.error('Network error: Check your connection or CORS settings.');
    } else if (error.status === 400 || error.status === 401) {
      this.notify.error('Invalid username or password.');
    } else {
      this.notify.error('Login failed. Please try again.');
    }
  }

  private isLoggingOut = false;

  /** Logout user */
  async logout(): Promise<void> {
    if (this.isLoggingOut) return; // prevent double logout
    this.isLoggingOut = true;

    const accessToken = this.storage.get<string>('access_token');

    try {
      if (accessToken) {
        const body = new HttpParams().set('token', accessToken);

        const headers = new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        });

        // Best-effort revoke
        await firstValueFrom(
          this.http.post(`${environment.AUTH_SERVER_URL}/o/revoke_token/`, body, { headers }),
        ).catch(() => null);
      }
    } catch {
      // swallow errors intentionally
    } finally {
      this.performLocalLogout();
    }
  }

  private performLocalLogout(): void {
    // Stop refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    //Clear local storage
    this.clearStore();

    // Notify user
    this.notify.success('Logged out successfully.');

    // Redirect
    window.location.href = '/home'; // Use window.location to force a full reload if needed, or stick to router
    // this.router.navigate(['/home'], { replaceUrl: true });

    this.isLoggingOut = false;
  }

  async setPassword(data: { password: string; token: string }): Promise<any> {
    try {
      const { data: result } = await firstValueFrom(
        this.apollo.mutate<any>({
          mutation: SET_NEW_PASSWORD,
          variables: {
            input: {
              password: data.password,
              token: data.token,
            },
          },
        }),
      );

      const response = result?.newUserPassword?.response;
      if (response?.status === 'SUCCESS' || response?.status === true || !response?.errors?.length) {
        this.notify.success(response?.message || 'Password set successfully!');
        return result?.newUserPassword;
      } else {
        const errorMsg = response?.errors?.[0]?.message || response?.message || 'Failed to set password';
        this.notify.error(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      this.handleError(error);
      throw error;
    }
  }

     async forgotPassword(email: string): Promise<any> {
       // Null check for email parameter
       if (!email) {
         const errorMsg = 'Email is required';
         this.notify.error(errorMsg);
         throw new Error(errorMsg);
       }
       
       try {
         console.log('Sending forgot password request for email:', email);
         
         const { data: result } = await firstValueFrom(
           this.apollo.mutate<any>({
             mutation: FORGOT_PASSWORD,
             variables: {
               email: email,
               input: {
                 // For forgot password flow, we don't need to set password yet
                 // This is just to satisfy the GraphQL schema requirement
                 // The actual password setting happens via the token link
               },
             },
           }),
         );

        console.log('Forgot password raw result:', result);
        
        const response = result?.forgotPassword?.response;
        console.log('Forgot password response:', response);
        
        if (response) {
          if (response.status === 'SUCCESS' || response.status === true || !response.errors?.length) {
            this.notify.success(response?.message || 'Password reset email sent successfully!');
            return result?.forgotPassword;
          } else {
            const errorMsg = response?.errors?.[0]?.message || response?.message || 'Failed to send password reset email';
            this.notify.error(errorMsg);
            throw new Error(errorMsg);
          }
        } else {
          const errorMsg = 'Invalid response from server - no response object';
          this.notify.error(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (error: any) {
        console.error('Forgot password error:', error);
        this.handleError(error);
        throw error;
      }
    }

     private handleError(error: any): void {
       const message = error.error?.message || error.message || 'An error occurred';
       // Log error for debugging purposes
       console.error('AuthService error:', error);
       this.notify.error(message);
     }

     async changePassword(oldPassword: string, newPassword: string): Promise<any> {
       try {
         const { data: result } = await firstValueFrom(
           this.apollo.mutate<any>({
             mutation: FORGOT_CHANGE_PASSWORD,
             variables: {
               input: {
                 oldPassword: oldPassword,
                 newPassword: newPassword,
               },
             },
           }),
         );

         const response = result?.forgotChangePassword?.response;
         if (response?.status === 'SUCCESS' || response?.status === true || !response?.errors?.length) {
           this.notify.success(response?.message || 'Password changed successfully!');
           return result?.forgotChangePassword;
         } else {
           const errorMsg = response?.errors?.[0]?.message || response?.message || 'Failed to change password';
           this.notify.error(errorMsg);
           throw new Error(errorMsg);
         }
       } catch (error: any) {
         this.handleError(error);
         throw error;
       }
     }



  async clearStore(): Promise<void> {
    this.storage.clearAll();
    this.storage.clearEverything();
    this.appStore.clearAll();
    this.dialog.closeAll();
    
    // Clear Apollo Cache
    try {
      await this.apollo.client.clearStore();
    } catch (e) {
      console.error('Error clearing Apollo store:', e);
    }
  }
}
